import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "../api";
import Shell from "../Shell";
import { authHeaders } from "../api";
import { tilesForRole } from "../navConfig";

function fmt(d) {
  if (!d) return "—";
  const dt = new Date(d);
  if (String(dt) === "Invalid Date") return d;
  const dd = String(dt.getDate()).padStart(2, "0");
  const mm = String(dt.getMonth() + 1).padStart(2, "0");
  const yyyy = dt.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
}

export default function IncidentsSummary() {
  const [role, setRole] = useState(null);
  const tiles = useMemo(() => tilesForRole(role), [role]);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const [q, setQ] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [inHouse, setInHouse] = useState("");
  const [onPhone, setOnPhone] = useState("");
  const [email, setEmail] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(false);

  const canView = role === "admin";

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/api/user/role/", {
          headers: authHeaders(),
        });
        setRole(data?.role || "staff");
      } catch {
        setRole("staff");
      }
    })();
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q.trim()) params.q = q.trim();
      if (fromDate) params.from = fromDate;
      if (toDate) params.to = toDate;
      if (inHouse !== "") params.in_house = inHouse;
      if (onPhone !== "") params.on_phone = onPhone;
      if (email !== "") params.email = email;

      const { data } = await api.get("/api/incidents/", {
        params,
        headers: authHeaders(),
      });

      setRows(Array.isArray(data) ? data : []);
    } finally {
      setLoading(false);
    }
  }, [q, fromDate, toDate, inHouse, onPhone, email]);

  useEffect(() => {
    if (canView) load();
  }, [canView, load]);

  if (role && !canView) {
    return (
      <Shell title="Hugarafl starfsfólk" rightMenu menuItems={tiles}>
        <h2 style={{ textAlign: "center", marginTop: 20 }}>Aðgangur óheimill</h2>
        <div style={{ textAlign: "center", marginTop: 8, opacity: 0.8 }}>
          Þessi síða er aðeins aðgengileg stjórnendum.
        </div>
      </Shell>
    );
  }

  return (
    <Shell title="Hugarafl starfsfólk" rightMenu menuItems={tiles}>
      <h2 style={{ textAlign: "center", margin: "8px 0 18px" }}>
        Yfirlit yfir atvik
      </h2>

      <div className="members-toolbar" style={{ justifyContent: "space-between" }}>
        <div
          className="search-block"
          style={{ display: "flex", gap: 8, alignItems: "center" }}
        >
          <label className="sr" htmlFor="incident-search">Leita</label>
          <input
            id="incident-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Leita í lýsingu, félagsaðila eða skráð af"
            style={{ width: 320 }}
          />
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div
            style={{
              border: "2px solid #222",
              borderRadius: 6,
              padding: "6px 12px",
              textAlign: "center",
              minWidth: 160,
              fontWeight: 600,
            }}
          >
            Atvik samtals
            <div style={{ fontSize: 18 }}>{rows.length}</div>
          </div>

          <button className="btn sm" onClick={load}>
            Uppfæra
          </button>

          <div className="filter-block">
            <button className="btn sm" onClick={() => setFiltersOpen((v) => !v)}>
              Sía
            </button>

            {filtersOpen && (
              <div className="filter-popover" style={{ minWidth: 340 }}>
                <div className="filter-title">Sía eftir dagsetningum:</div>

                <div className="filter-row">
                  <label>Frá:</label>
                  <input
                    type="date"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                  />
                </div>

                <div className="filter-row">
                  <label>Til:</label>
                  <input
                    type="date"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                  />
                </div>

                <div className="filter-title" style={{ marginTop: 10 }}>
                  Aðrar síur:
                </div>

                <div className="filter-row">
                  <label>Í hús:</label>
                  <select value={inHouse} onChange={(e) => setInHouse(e.target.value)}>
                    <option value="">Allt</option>
                    <option value="1">Já</option>
                    <option value="0">Nei</option>
                  </select>
                </div>

                <div className="filter-row">
                  <label>Sími:</label>
                  <select value={onPhone} onChange={(e) => setOnPhone(e.target.value)}>
                    <option value="">Allt</option>
                    <option value="1">Já</option>
                    <option value="0">Nei</option>
                  </select>
                </div>

                <div className="filter-row">
                  <label>Email:</label>
                  <select value={email} onChange={(e) => setEmail(e.target.value)}>
                    <option value="">Allt</option>
                    <option value="1">Já</option>
                    <option value="0">Nei</option>
                  </select>
                </div>

                <div
                  style={{
                    display: "flex",
                    gap: 8,
                    justifyContent: "flex-end",
                    marginTop: 10,
                  }}
                >
                  <button
                    className="btn ghost sm"
                    onClick={() => {
                      setQ("");
                      setFromDate("");
                      setToDate("");
                      setInHouse("");
                      setOnPhone("");
                      setEmail("");
                    }}
                  >
                    Hreinsa síur
                  </button>

                  <button
                    className="btn ghost sm"
                    onClick={() => setFiltersOpen(false)}
                  >
                    Loka
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="loading">Hleð…</div>
      ) : (
        <>
          <div className="table-wrap">
            <table className="table zebra">
              <thead>
                <tr>
                  <th>Dagsetning</th>
                  <th>Félagsaðili</th>
                  <th>Skráð af</th>
                  <th>Í hús</th>
                  <th>Sími</th>
                  <th>Email</th>
                  <th>Tími</th>
                  <th>Lýsing</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={8} style={{ textAlign: "center", padding: 16 }}>
                      Engin atvik fundust
                    </td>
                  </tr>
                ) : (
                  rows.map((x) => (
                    <tr key={x.id}>
                      <td>{fmt(x.incident_date)}</td>
                      <td>{x.user_name || "—"}</td>
                      <td>{x.created_by_name || "—"}</td>
                      <td>{x.in_house ? "Já" : "Nei"}</td>
                      <td>{x.on_phone ? "Já" : "Nei"}</td>
                      <td>{x.email ? "Já" : "Nei"}</td>
                      <td>{x.time_taken || "—"}</td>
                      <td style={{ maxWidth: 420, whiteSpace: "pre-wrap" }}>
                        {x.description || "—"}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div style={{ textAlign: "center", marginTop: 10, opacity: 0.8 }}>
            Samtals í lista: {rows.length}
          </div>
        </>
      )}
    </Shell>
  );
}